import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { WIZARD_STEPS, type WizardStepId } from './wizard-steps';

/**
 * The horizontal step bar (UI spec §6): full width, 1px border, radius 6px, each
 * step `flex: 1` with a 1px right divider. The active step gets the `--accent-soft`
 * background, `--accent-hover` text at 600 weight and the `inset 0 -2px 0 --accent`
 * underline; a completed step is prefixed with a `✓` in `--ok`.
 *
 * Steps are **directly clickable** — there is no forced linear progression (UI
 * spec §6), so every step is a real button that jumps straight to its panel.
 *
 * ## What the markers mean
 *
 * Two different facts, deliberately not conflated:
 *
 * - `✓` means the user pressed Continue on that step. It is a trail of where they
 *   have been, not a verdict on the contents.
 * - `!` means the step still has a required input that is empty, and it is
 *   **withheld from the tick**: a step that owes input never shows `✓`, because a
 *   tick beside an incomplete step is the misleading part. Only the tick is
 *   suppressed; the user's progress is not forgotten, so filling the field in
 *   restores the tick they earned.
 *
 * The marker is never the only carrier of either fact: each tab's accessible name
 * states the outstanding count in words, so the glyph is redundant rather than
 * load-bearing (R18.8).
 */
export function WizardStepper({
  activeStep,
  completedSteps,
  missingCountByStep,
  onSelect,
}: {
  readonly activeStep: WizardStepId;
  readonly completedSteps: ReadonlySet<WizardStepId>;
  /**
   * How many required inputs each step still owes; a step with nothing missing is
   * absent. Empty map when the caller tracks no completeness, which renders the
   * bar exactly as before.
   */
  readonly missingCountByStep?: ReadonlyMap<WizardStepId, number>;
  readonly onSelect: (step: WizardStepId) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      role="tablist"
      aria-label={t('intake.nav.stepper')}
      className="border-line flex overflow-hidden rounded-[6px] border"
    >
      {WIZARD_STEPS.map((step, index) => {
        const active = step.id === activeStep;
        const missingCount = missingCountByStep?.get(step.id) ?? 0;
        const hasMissing = missingCount > 0;
        // A step that still owes input does not get to claim it is done, so the
        // tick yields to the `!`. Pressing Continue is still remembered.
        const completed = completedSteps.has(step.id) && !hasMissing;
        const label = t(step.labelKey);
        return (
          <button
            key={step.id}
            type="button"
            role="tab"
            aria-selected={active}
            // The outstanding count in words, so the marker is redundant to a
            // screen reader rather than the only signal (R18.8).
            {...(hasMissing
              ? {
                  'aria-label': t('intake.nav.stepMissing', { step: label, count: missingCount }),
                  title: t('intake.nav.stepMissing', { step: label, count: missingCount }),
                }
              : {})}
            onClick={() => onSelect(step.id)}
            className={cn(
              'flex-1 px-2 py-2 text-left text-[11px] transition-colors',
              index < WIZARD_STEPS.length - 1 && 'border-line border-r',
              active
                ? 'bg-[var(--accent-soft)] font-semibold text-[var(--accent-hover)] shadow-[inset_0_-2px_0_var(--accent)]'
                : 'bg-panel text-muted-foreground hover:bg-surface-alt',
            )}
          >
            {completed && !active ? (
              <span className="mr-1 text-[var(--ok)]" aria-hidden>
                {t('intake.nav.completed')}
              </span>
            ) : null}
            {hasMissing ? (
              // The governance tier's own glyph and colour (UI spec §3.8): an
              // incomplete step blocks submission exactly as a governance finding
              // does, so it reads in the same visual language as the summary's
              // advisory rather than inventing a third state.
              <span className="mr-1 font-semibold text-[#7D5300]" aria-hidden>
                {t('intake.nav.hasMissing')}
              </span>
            ) : null}
            <span className="tabular-nums">{index + 1}</span> {label}
          </button>
        );
      })}
    </div>
  );
}
